import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, PenLine, Trash2 } from 'lucide-react';
import Layout from '../../components/Layout';
import departmentService from '../../services/departmentService';
import './DepartmentDetailPage.css';

const DepartmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [department, setDepartment] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDepartment();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadDepartment = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await departmentService.getDepartmentById(parseInt(id || '0'));
      setDepartment(data);
      setLoading(false);
    } catch (err) {
      setError(t('department.failedToLoadDepartment'));
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (!department) return;
    navigate(`/organization/departments/${department.departmentId}/delete`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="detail-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!department) {
    return (
      <Layout>
        <div className="detail-container">
          <div className="alert alert-danger">{t('department.failedToLoadDepartment')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="detail-container">
        {/* Navigation Breadcrumb */}
        <nav className="detail-breadcrumb">
          <button
            className="detail-breadcrumb-btn"
            onClick={() => navigate('/organization/departments')}
            title={t('department.backToDepartments')}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('department.title')}
          </button>
        </nav>

        {/* Page Title */}
        <div className="detail-header">
          <h1 className="detail-title">{department.departmentName}</h1>
          <p className="detail-subtitle">{t('department.viewTitle')}</p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
          </div>
        )}

        <div className="detail-content">
          {/* Details Section */}
          <div className="detail-main-section">
            {/* Department Details Card */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('department.details')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <label className="detail-label">{t('department.departmentName')}</label>
                    <p className="detail-value">{department.departmentName}</p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('department.type')}</label>
                    <div className="detail-value">
                      <span className="info-badge info-badge-active">{department.departmentType}</span>
                    </div>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('department.code')}</label>
                    <p className="detail-value">{department.departmentCode}</p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('department.shortName')}</label>
                    <p className="detail-value">{department.departmentShortName || 'N/A'}</p>
                  </div>

                  <div className="detail-field detail-field-full">
                    <label className="detail-label">{t('common.description')}</label>
                    <p className="detail-value detail-description">
                      {department.description || (
                        <em className="detail-empty">{t('department.noDescriptionProvided')}</em>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Information Card */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h2 className="detail-card-title">{t('common.info')}</h2>
              </div>
              <div className="detail-card-body">
                <div className="detail-grid">
                  <div className="detail-field">
                    <label className="detail-label">ID</label>
                    <p className="detail-value detail-code">{department.departmentId}</p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('common.status')}</label>
                    <div className="detail-value">
                      <span className="info-badge info-badge-active">
                        {department.isActive ? t('common.active') : t('common.inactive')}
                      </span>
                    </div>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('common.createdDate')}</label>
                    <p className="detail-value">
                      {new Date(department.createdDate).toLocaleDateString(
                        i18n.language === 'ja' ? 'ja-JP' : 'en-US'
                      )}
                    </p>
                  </div>

                  <div className="detail-field">
                    <label className="detail-label">{t('common.createdTime')}</label>
                    <p className="detail-value">
                      {new Date(department.createdDate).toLocaleTimeString(
                        i18n.language === 'ja' ? 'ja-JP' : 'en-US'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions Sidebar */}
          <div className="detail-sidebar">
            {/* Actions Card */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('common.actions')}</h3>
              </div>
              <div className="detail-card-body detail-actions">
                <button
                  className="btn-save"
                  onClick={() => navigate(`/organization/departments/${department.departmentId}/edit`)}
                  title={t('department.editDepartment')}
                >
                  <PenLine size={16} strokeWidth={2} />
                  {t('common.edit')}
                </button>

                <button
                  className="btn-delete"
                  onClick={handleDelete}
                  title={t('department.deleteDepartment')}
                >
                  <Trash2 size={16} strokeWidth={2} />
                  {t('common.delete')}
                </button>

                <div className="detail-divider"></div>

                <button
                  className="detail-btn detail-btn-secondary"
                  onClick={() => navigate('/organization/departments')}
                  title={t('department.backToDepartments')}
                >
                  <ArrowLeft size={16} strokeWidth={2} />
                  {t('department.backToDepartments')}
                </button>
              </div>
            </div>

            {/* Quick Actions Card */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">{t('common.quickActions')}</h3>
              </div>
              <div className="detail-card-body">
                <button
                  className="detail-quick-link"
                  onClick={() => navigate(`/organization/departments/${department.departmentId}/edit`)}
                >
                  <span className="detail-quick-icon">→</span>
                  <span>{t('common.edit')}</span>
                </button>
                <button
                  className="detail-quick-link"
                  onClick={() => navigate('/organization/departments')}
                >
                  <span className="detail-quick-icon">→</span>
                  <span>{t('department.backToDepartments')}</span>
                </button>
                <button
                  className="detail-quick-link"
                  onClick={() => navigate('/organization/departments/create')}
                >
                  <span className="detail-quick-icon">→</span>
                  <span>{t('department.newDepartment')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DepartmentDetailPage;
